import { flow, Instance, types } from 'mobx-state-tree';
import moment from 'moment';
import { getEnvironment } from '.././utils';
import Cookie from 'js-cookie';

export const PharmacyFilters = types.model({
  pharmaciesActiveFilter: types.maybe(types.string),
  pharmaciesLevelFilter: types.maybe(types.string),
  pharmacyYearFirstLine: types.number,
  pharmacyYearSecondLine: types.number,
  lastSelectedPharmacy: types.maybe(types.number),
  diagnosticPageActiveTab: types.maybe(types.string),
  diagnosticPageActiveSubTab: types.maybe(types.string),
  diagnosticPageStartDate: types.optional(types.maybeNull(types.Date), undefined),
  diagnosticPageEndDate: types.maybeNull(types.Date),
  diagnosticPageCurrentPlanningId: types.number,
  diagnosticOffset: types.number,
  pharmacyPageActiveTab: types.maybe(types.string),
  showDutiesOnMapDate: types.number,
});

export const DutyMapFilters = types.model({
  showPharmacyDutyCircle: types.boolean,
  showPharmacyDuty30kmCircle: types.boolean,
  showAnchor: types.boolean,
  showDayDuties: types.boolean,
  showNightDuties: types.boolean,
  showAllSecretaryDuties: types.boolean,
  showAllCloseDuties: types.boolean,
  selectedBoxes: types.array(types.model({ label: types.string, value: types.string })),
});

export const UiStore = types
  .model('UiStore', {
    pharmaciesActiveFilter: 'active',
    pharmaciesLevelFilter: 'all',
    pharmaciesSecretariatIdFilter: types.optional(types.union(types.number, types.undefined), () => 0),
    pharmacyFirstLineYear: moment().year(),
    pharmacySecondLineYear: moment().year(),
    lastSelectedPharmacy: types.optional(types.maybe(types.number), undefined),
    diagnosticPageActiveTab: '',
    diagnosticPageActiveSubTab: '',
    diagnosticPageActiveSubSubTab: '',
    diagnosticPageFirstDate: types.optional(types.maybeNull(types.Date), undefined),
    diagnosticPageStartDate: types.optional(types.maybeNull(types.Date), undefined),
    diagnosticPageEndDate: types.optional(types.maybeNull(types.Date), undefined),
    diagnosticPageCurrentPlanningId: -1,
    diagnosticPagePrevPlanningId: -1,
    diagnosticPageCurrentSemesterYear: new Date().getFullYear(),
    diagnosticOffset: -1,
    diagnosticSearchStatus: types.optional(types.model({ label: types.string, value: types.string }), {
      label: 'publié',
      value: 'published',
    }),
    pharmacyPageActiveTab: '',
    showDutiesOnMapDate: moment().format('YYYY-MM-DD'),
    centerCoordinates: types.optional(types.model({ lat: types.number, lng: types.number }), {
      lat: 50.58603,
      lng: 3.806026,
    }),
    filteredAndSortedPharmacies: types.array(types.number),
    multiCalendarFirstPharmacyId: types.maybe(types.number),
    multiCalendarSecondPharmacyId: types.maybe(types.number),
    dutyMapPageFilters: DutyMapFilters,
    lowerGapLimit: types.maybe(types.number),
    upperGapLimit: types.maybe(types.number),
    lowerDistanceLimit: types.maybe(types.number),
  })
  .actions((self) => {
    const env = getEnvironment(self);
    const cookieName = `${env.config.configName}_web_gardes_uistore_state`;

    const persistUiState = function() {
      Cookie.set(cookieName, JSON.stringify(self), { expires: 365, path: '/', sameSite: 'Lax' });
    };

    const afterAttach = function() {
      const cookieStr = Cookie.get(cookieName);
      if (cookieStr) {
        const cookie = JSON.parse(cookieStr);
        if (cookie) {
          self.lowerDistanceLimit = parseInt(cookie.lowerDistanceLimit ? cookie.lowerDistanceLimit : 1000, 10);
          self.lowerGapLimit = parseInt(cookie.lowerGapLimit ? cookie.lowerGapLimit : 4, 10);
          self.upperGapLimit = parseInt(cookie.upperGapLimit ? cookie.upperGapLimit : 50, 10);
        }
      }
    };

    const setLowerDistanceLimit = function(limit) {
      self.lowerDistanceLimit = limit;
    };

    const setLowerGapLimit = function(limit) {
      self.lowerGapLimit = limit;
    };

    const setUpperGapLimit = function(limit) {
      self.upperGapLimit = limit;
    };

    const setPharmaciesActiveFilter = function(filter) {
      self.pharmaciesActiveFilter = filter;
    };

    const setPharmaciesLevelFilter = function(filter) {
      self.pharmaciesLevelFilter = filter;
    };

    const setPharmaciesSecretariatIdFilter = function(filter: number | undefined) {
      self.pharmaciesSecretariatIdFilter = filter;
    };

    const setPharmacyCalendarYear = function(year) {
      self.pharmacyFirstLineYear = year;
      self.pharmacySecondLineYear = year;
    };

    const setDiagnosticPageActiveTab = function(tab) {
      self.diagnosticPageActiveTab = tab;
    };

    const setDiagnosticPageActiveSubTab = function(tab) {
      self.diagnosticPageActiveSubTab = tab;
    };

    const setDiagnosticPageActiveSubSubTab = function(tab) {
      self.diagnosticPageActiveSubSubTab = tab;
    };

    const setDiagnosticPageFirstDate = function(date) {
      self.diagnosticPageFirstDate = date;
    };

    const setDiagnosticPageStartDate = function(date) {
      self.diagnosticPageStartDate = date;
    };

    const setDiagnosticPageEndDate = function(date) {
      self.diagnosticPageEndDate = date;
    };

    const setDiagnosticPageCurrentPlanningId = function(id) {
      self.diagnosticPageCurrentPlanningId = id;
    };
    const setDiagnosticPageCurrentSemesterYear = function(year) {
      self.diagnosticPageCurrentSemesterYear = year;
    };
    const setPharmacyPageActiveTab = function(tab) {
      self.pharmacyPageActiveTab = tab;
    };

    const setShowDutiesOnMapDate = function(showDate) {
      self.showDutiesOnMapDate = showDate;
    };

    const setLastSelectedPharmacy = function(pharmacyId) {
      self.lastSelectedPharmacy = pharmacyId;
    };

    const setCenterCoordinates = function(coordinates) {
      self.centerCoordinates = coordinates;
    };

    const setDiagnosticOffset = function(offset) {
      self.diagnosticOffset = offset;
    };

    const setDiagnosticPagePrevPlanningId = function(prevPlanningId) {
      self.diagnosticPagePrevPlanningId = prevPlanningId;
    };

    const setDiagnosticSearchStatus = function(status) {
      self.diagnosticSearchStatus = status;
    };

    const setFilteredAndSortedPharmacies = function(pharmacyIds) {
      self.filteredAndSortedPharmacies = pharmacyIds;
    };

    const getNextNavigationPharmacy = function(currentPharmacyId) {
      var currentIndex = self.filteredAndSortedPharmacies.indexOf(currentPharmacyId);
      var nextPharmacyId = self.filteredAndSortedPharmacies[currentIndex + 1];
      return nextPharmacyId;
    };

    const getPreviousNavigationPharmacy = function(currentPharmacyId) {
      var currentIndex = self.filteredAndSortedPharmacies.indexOf(currentPharmacyId);
      var nextPharmacyId = self.filteredAndSortedPharmacies[currentIndex - 1];
      return nextPharmacyId;
    };

    const setMultiCalendarFirstPharmacyId = function(pharmacyId) {
      self.multiCalendarFirstPharmacyId = pharmacyId;
    };

    const setMultiCalendarSecondPharmacyId = function(pharmacyId) {
      self.multiCalendarSecondPharmacyId = pharmacyId;
    };

    const setDutyMapFilters = function(dutyMapFilters: Instance<typeof DutyMapFilters>) {
      self.dutyMapPageFilters = dutyMapFilters;
    };

    return {
      afterAttach, //: flow(function*() {}),
      setPharmaciesActiveFilter,
      setPharmaciesLevelFilter,
      setPharmaciesSecretariatIdFilter,
      setPharmacyCalendarYear,
      setDiagnosticPageActiveTab,
      setDiagnosticPageActiveSubTab,
      setDiagnosticPageActiveSubSubTab,
      setDiagnosticPageStartDate,
      setDiagnosticPageFirstDate,
      setDiagnosticPageEndDate,
      setDiagnosticPageCurrentPlanningId,
      setDiagnosticPagePrevPlanningId,
      setDiagnosticPageCurrentSemesterYear,
      setDiagnosticSearchStatus,
      setPharmacyPageActiveTab,
      setShowDutiesOnMapDate,
      setLastSelectedPharmacy,
      setCenterCoordinates,
      setDiagnosticOffset,
      setFilteredAndSortedPharmacies,
      getNextNavigationPharmacy,
      getPreviousNavigationPharmacy,
      setMultiCalendarFirstPharmacyId,
      setMultiCalendarSecondPharmacyId,
      setDutyMapFilters,
      persistUiState,
      setLowerGapLimit,
      setUpperGapLimit,
      setLowerDistanceLimit,
    };
  });
